import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
  Link as MuiLink,
} from '@mui/material';
import {
  Lock as LockIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import authApi from '../api';
import type { RegistrationRequest } from '@/types/auth';
import { useLoading } from '@/hooks/useLoading';
import FullScreenLoading from '@/components/common/FullLoading';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import ParticleBackground from '@/components/common/ParticleBackground';
import { PASSWORD_MIN_LENGTH, PASSWORD_PATTERN, EMAIL_PATTERN } from '@/constants/auth';

// Password strength validation rules (based on API documentation)
const passwordRules = {
  minLength: {
    pattern: new RegExp(`.{${PASSWORD_MIN_LENGTH},}`),
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
  },
  uppercase: { pattern: PASSWORD_PATTERN.uppercase, label: 'At least 1 uppercase letter' },
  lowercase: { pattern: PASSWORD_PATTERN.lowercase, label: 'At least 1 lowercase letter' },
  number: { pattern: PASSWORD_PATTERN.number, label: 'At least 1 digit' },
};

interface RegisterFormData extends RegistrationRequest {
  confirmPassword: string;
}

// ── Shared input styles (matches LoginPage) ──
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    background: 'rgba(255,255,255,0.05)',
    color: 'rgba(255,255,255,0.9)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#00B8D4' },
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(255,255,255,0.4)',
    opacity: 1,
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: 'rgba(255,255,255,0.4)',
  },
};

export default function RegisterPage() {
  const { t } = useTranslation('auth');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<Record<string, boolean>>({});
  const { loading, setLoading } = useLoading();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterFormData>({
    mode: 'onBlur',
  });

  const passwordValue = watch('password');
  const confirmPasswordValue = watch('confirmPassword');

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pwd = e.target.value;
    const strength: Record<string, boolean> = {};
    Object.entries(passwordRules).forEach(([key, rule]) => {
      strength[key] = rule.pattern.test(pwd);
    });
    setPasswordStrength(strength);
  };

  const isPasswordValid = Object.values(passwordStrength).every((v) => v);

  const registerMutation = useMutation({
    mutationFn: (values: RegisterFormData) =>
      authApi.register({
        email: values.email,
        password: values.password,
        name: values.name,
      }),
    onMutate: () => setLoading(true),
    onSuccess: () => {
      toast.success(t('register.registerSuccess'));
      setLoading(false);
      navigate('/login');
    },
    onError: () => {
      setLoading(false);
    },
  });

  const onSubmit = (data: RegisterFormData) => {
    if (data.password !== data.confirmPassword) {
      toast.error(t('register.validation.passwordMismatch'));
      return;
    }
    if (!isPasswordValid) {
      toast.error(t('register.validation.passwordRequirementsNotMet'));
      return;
    }
    registerMutation.mutate(data);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        background: '#0a0a0a',
      }}
    >
      <FullScreenLoading open={loading} />

      {/* ── Left: Form Panel ──────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 3, sm: 6 },
          py: 4,
          position: 'relative',
        }}
      >
        {/* Top bar: Home + controls */}
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            left: 24,
            right: 24,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <MuiLink
            href="/"
            sx={{
              color: 'rgba(255,255,255,0.6)',
              textDecoration: 'none',
              fontSize: '0.95rem',
              '&:hover': { color: '#fff' },
              transition: 'color 0.2s',
            }}
          >
            ← {t('login.backToHome')}
          </MuiLink>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <LanguageSwitcher />
          </Box>
        </Box>

        {/* Form content */}
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          {/* Logo + Title */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 1 }}>
            <Typography
              sx={{
                fontFamily:
                  'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                fontWeight: 400,
                fontSize: '1.8rem',
                color: '#fff',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
              }}
            >
              {t('register.title')}
            </Typography>
            <Box
              component="img"
              src="/intellio-wordmark-dark.svg"
              alt="Intellio AI"
              sx={{ height: 40, mb: '-4px', ml: -1 }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily:
                'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.9rem',
              fontWeight: 500,
              mb: 4,
            }}
          >
            {t('register.subtitle')}
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              {/* Name field */}
              <TextField
                fullWidth
                placeholder={t('register.name')}
                error={!!errors.name}
                helperText={errors.name?.message}
                sx={inputSx}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  },
                }}
                {...register('name', {
                  required: t('register.validation.nameRequired'),
                  minLength: {
                    value: 2,
                    message: t('register.validation.nameMinLength', { count: 2 }),
                  },
                  maxLength: {
                    value: 100,
                    message: t('register.validation.nameMaxLength', { count: 100 }),
                  },
                })}
              />

              {/* Email field */}
              <TextField
                fullWidth
                type="email"
                placeholder={t('register.email')}
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={inputSx}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  },
                }}
                {...register('email', {
                  required: t('register.validation.emailRequired'),
                  pattern: {
                    value: EMAIL_PATTERN,
                    message: t('register.validation.emailInvalid'),
                  },
                })}
              />

              {/* Password field */}
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                placeholder={t('register.password')}
                error={!!errors.password}
                helperText={errors.password?.message}
                sx={inputSx}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: passwordValue ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          tabIndex={-1}
                          sx={{ color: 'rgba(255,255,255,0.4)' }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
                {...register('password', {
                  required: t('register.validation.passwordRequired'),
                  minLength: {
                    value: 8,
                    message: t('register.validation.passwordMinLength', { count: 8 }),
                  },
                  onChange: handlePasswordChange,
                })}
              />

              {/* Password strength indicator */}
              {passwordValue && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mb: 1, color: 'rgba(255,255,255,0.5)' }}
                  >
                    {t('register.passwordRules.title')}
                  </Typography>
                  <Stack spacing={0.5}>
                    {Object.entries(passwordRules).map(([key]) => (
                      <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {passwordStrength[key] ? (
                          <CheckIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                        ) : (
                          <CloseIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: passwordStrength[key] ? '#22c55e' : 'rgba(255,255,255,0.4)',
                          }}
                        >
                          {t(`register.passwordRules.${key}`, {
                            count: key === 'minLength' ? PASSWORD_MIN_LENGTH : undefined,
                          })}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Confirm password field */}
              <TextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('register.confirmPassword')}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                sx={inputSx}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: confirmPasswordValue ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                          tabIndex={-1}
                          sx={{ color: 'rgba(255,255,255,0.4)' }}
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
                {...register('confirmPassword', {
                  required: t('register.validation.confirmPasswordRequired'),
                })}
              />

              {/* Register button */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={registerMutation.isPending || !isPasswordValid}
                sx={{
                  mt: 1,
                  py: 1.5,
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  bgcolor: '#00B8D4',
                  color: '#000',
                  '&:hover': { bgcolor: '#0097b2', transform: 'translateY(-1px)' },
                  '&:disabled': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.3)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {registerMutation.isPending
                  ? t('register.registering')
                  : t('register.registerButton')}
              </Button>

              {/* Return to login link */}
              <Typography
                sx={{
                  textAlign: 'center',
                  mt: 2,
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '0.85rem',
                }}
              >
                {t('register.haveAccount')}{' '}
                <MuiLink
                  href="/login"
                  sx={{
                    color: '#00B8D4',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('register.loginNow')}
                </MuiLink>
              </Typography>
            </Stack>
          </form>
        </Box>
      </Box>

      {/* ── Right: Visual Panel (hidden on mobile) ── */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flex: 1,
          alignItems: 'flex-start',
          justifyContent: 'center',
          pt: '38vh',
          position: 'relative',
          m: 2,
          borderRadius: 4,
          overflow: 'hidden',
          background: '#0a0a0a',
        }}
      >
        <ParticleBackground />

        <Box sx={{ position: 'relative', textAlign: 'center', px: 6, zIndex: 1 }}>
          <Typography
            sx={{
              fontFamily:
                'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              fontWeight: 600,
              fontSize: { lg: '2.6rem', xl: '3rem' },
              color: '#fff',
              lineHeight: 1.15,
              mb: 2,
              letterSpacing: '-0.02em',
            }}
          >
            {t('login.tagline1')}
            <Box component="span" sx={{ color: '#00B8D4', ml: 2.5 }}>
              {t('login.tagline2')}
            </Box>
          </Typography>
          <Typography
            sx={{
              fontFamily:
                'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '1rem',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.5,
            }}
          >
            {t('login.taglineDescription')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
